import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { TID } from "@/constants/testIds";
import { Truck, AlertTriangle } from "lucide-react";
import { fmt, fmtMoney, KPI } from "./_shared";

export function FleetTab({ fleet }) {
  if (!fleet) return <div className="h-40 rounded-lg bg-white border border-slate-200 shimmer" />;
  const t = fleet.fleet_totals;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-3">
        <Truck className="w-5 h-5 text-amber-700 mt-0.5" />
        <div className="text-sm text-amber-900">
          <div className="font-semibold">Fleet Efficiency — from your Ampol data</div>
          <p className="text-xs mt-0.5">
            Rankings, worst-10 emitters, and vs-fleet-average variance. Cost data appears when Ampol commercial CSVs are uploaded.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Active Vehicles"   value={fmt(t.vehicle_count)}                icon={Truck} tone="brand" />
        <KPI label="Total Litres"      value={fmt(t.litres)}                        tone="default" />
        <KPI label="Total CO2e (kg)"   value={fmt(t.co2e_kg)}                       tone="danger" />
        <KPI label="Fuel Spend"        value={t.cost_inc_gst > 0 ? fmtMoney(t.cost_inc_gst) : "n/a"} hint="requires cost-column CSV" />
        <KPI label="Avg L / Vehicle"   value={fmt(fleet.avg_litres_per_vehicle, 1)} tone="warn" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-4 col-span-2">
          <div className="text-sm font-medium text-slate-800 mb-2">Fleet Fuel — Monthly</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={(fleet.by_month || []).map((m) => ({ ...m, label: m.period_month.slice(5) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
              <YAxis yAxisId="v" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: "12px" }} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Line yAxisId="l" type="monotone" dataKey="litres" name="Litres" stroke="#00334C" strokeWidth={2.5} />
              <Line yAxisId="v" type="monotone" dataKey="active_vehicles" name="Active vehicles" stroke="#22C55E" strokeWidth={2} strokeDasharray="4 3" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-rose-200 bg-white p-4">
          <div className="text-sm font-medium text-rose-900 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4" /> Worst 10 by CO2e
          </div>
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {(fleet.worst_by_co2e || []).map((v, idx) => (
              <div key={v.rego} data-testid={TID.fleetWorstRow(v.rego)} className="py-1.5 flex items-center gap-2 text-xs">
                <span className="w-5 text-slate-400">{idx + 1}</span>
                <span className="font-mono text-slate-800 flex-1">{v.rego}</span>
                <span className="text-rose-700 font-semibold">{fmt(v.co2e_kg)} kg</span>
                <span className="text-slate-500">({v.vs_fleet_avg_pct >= 0 ? "+" : ""}{v.vs_fleet_avg_pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="grid grid-cols-8 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-emerald-800">
          <div>Rego</div>
          <div className="text-right">Litres</div>
          <div className="text-right">CO2e (kg)</div>
          <div className="text-right">Cost inc GST</div>
          <div className="text-right">Txns</div>
          <div className="text-right">km</div>
          <div className="text-right">L/100km</div>
          <div className="text-right">vs Fleet Avg</div>
        </div>
        <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {(fleet.vehicles || []).map((v) => {
            const varianceTone = v.vs_fleet_avg_pct > 50 ? "text-rose-700" : v.vs_fleet_avg_pct > 0 ? "text-amber-700" : "text-emerald-700";
            return (
              <div key={v.rego} data-testid={TID.fleetVehicleRow(v.rego)} className="grid grid-cols-8 gap-2 px-4 py-2 text-sm">
                <div className="font-mono">{v.rego}</div>
                <div className="text-right">{fmt(v.litres)}</div>
                <div className="text-right text-rose-700">{fmt(v.co2e_kg)}</div>
                <div className="text-right">{v.cost_inc_gst > 0 ? fmtMoney(v.cost_inc_gst) : "—"}</div>
                <div className="text-right text-slate-500">{v.transactions}</div>
                <div className="text-right text-slate-500">{v.km_driven > 0 ? fmt(v.km_driven) : "—"}</div>
                <div className="text-right">{v.l_per_100km ? v.l_per_100km : "—"}</div>
                <div className={`text-right font-medium ${varianceTone}`}>{v.vs_fleet_avg_pct >= 0 ? "+" : ""}{v.vs_fleet_avg_pct}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
